import { ZONE_STYLES } from '@m1/ol-map';
import OlMap from '@m1/ol-map';
import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import Feature from 'ol/Feature';
import { Polygon } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import { fromLonLat } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import React, { useState, useRef, useMemo } from 'react';
import { RiCheckboxBlankLine, RiCheckboxFill, RiMapPin5Line, RiAddLine, RiDeleteBin2Line } from 'react-icons/ri';
import { useSelector, useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import AccordionCaret from './AccordionCaret';
import EmptyList from './EmptyList';
import styles from './ZonesSelector.module.scss';

import geoZones from '@/define/geoZones';
import { NotifierService as notifier } from '@/libs/Notifier';

const cx = classNames.bind(styles);

const Zones = () => {
  const store = useStore();
  const userId = useSelector((state) => state.user?.id);
  const [showPanel, setShowPanel] = useState(false);
  const [localZones, setLocalZones] = useState([]);
  const [customZones, setCustomZones] = useState([]);
  const menuRef = useRef();
  const panelRef = useRef();
  const fileInputRef = useRef();
  const zones = useRef({});
  const map = OlMap.getMap();

  const isKoreanLocale = /ko|kr/i.test(navigator.language);

  const calculateChecksum = async (content) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const count = useMemo(() => {
    const localCount = localZones.filter((zone) => zone.on).length;
    const customCount = customZones.filter((zone) => zone.on).length;
    return localCount + customCount;
  }, [localZones, customZones]);

  useMountEffect(() => {
    if (!isKoreanLocale) return;

    const names = Object.keys(geoZones);
    const promises = names.map((name) => {
      return import(`@/assets/vworld/${name}.json`);
    });

    Promise.all(promises).then((results) => {
      const nextLocalZones = results.map((result, index) => ({
        id: uuidv4(),
        ...geoZones[names[index]],
        features: result.default.features,
        on: false,
      }));

      setLocalZones(nextLocalZones);
    });
  });

  useUpdateEffect(() => {
    const saved = sessionStorage.getItem(`@zones/${userId}`);
    if (!saved) return;

    const nextCustomZones = JSON.parse(saved).map((zone) => ({ ...zone, on: false }));
    setCustomZones(nextCustomZones);
  }, [userId]);

  useUpdateEffect(() => {
    if (showPanel) {
      document.addEventListener('mousedown', unfocusPanel);
    }

    return () => {
      document.removeEventListener('mousedown', unfocusPanel);
    };
  }, [showPanel]);

  const saveCustomZonesToStorage = (zones) => {
    const dataToSave = zones.map(({ on, ...rest }) => rest);
    sessionStorage.setItem(`@zones/${userId}`, JSON.stringify(dataToSave));
  };

  const getRandomHexColor = () => {
    const randomColor = Math.floor(Math.random() * 16777215).toString(16);
    return '#' + randomColor.padStart(6, '0');
  };

  const createFeatureLayer = (features, { title, color }) => {
    const layer = new VectorLayer({
      source: new VectorSource({ features, wrapX: false }),
      isZone: true,
      style: ZONE_STYLES(title, color),
    });

    map.addLayer(layer);
    return layer;
  };

  const showZone = ({ id, title, color, features }) => {
    const olFeatures = features.map(({ geometry }) => {
      let coordinates;
      if (geometry.type === 'MultiPolygon') {
        coordinates = geometry.coordinates[0][0].map((coord) => fromLonLat(coord));
      } else if (geometry.type === 'Polygon') {
        coordinates = geometry.coordinates[0].map((coord) => fromLonLat(coord));
      }

      return new Feature(new Polygon([coordinates]));
    });
    zones.current[id] = createFeatureLayer(olFeatures, { title, color });
  };

  const hideZone = ({ id }) => {
    const layer = zones.current[id];

    map.removeLayer(layer);
    delete zones.current[id];
  };

  const toggleZone = (e, zone, isCustom = false) => {
    e.stopPropagation();

    if (!zone.on) {
      // 데이터 로드 실패 시 에러 처리
      if (zone.features === null) {
        notifier.error('Failed to load the zone.');
        return;
      }

      showZone(zone);
    } else {
      hideZone(zone);
    }

    // toggle 상태 변경
    zone.on = !zone.on;

    // state 업데이트로 리렌더링
    if (isCustom) {
      setCustomZones([...customZones]);
    } else {
      setLocalZones([...localZones]);
    }
  };

  const uploadCustomZoneFile = async (e) => {
    // 파일 읽기 및 JSON 파싱
    const file = e.target.files[0];
    const data = await file.text();
    const jsonData = JSON.parse(data);

    // 필수 데이터 구조 검증
    if (jsonData.type !== 'FeatureCollection' || !jsonData.features) {
      notifier.error('Invalid file format. Please check your file and try again.');
      e.target.value = '';
      return;
    }

    //#region 중복 여부 검사
    // checksum 계산
    const checksum = await calculateChecksum(data);
    // checksum으로 중복 zone 체크
    const isExist = customZones.some((zone) => zone.checksum === checksum);

    if (isExist) {
      notifier.error('This zone already exists.');
      e.target.value = '';
      return;
    }
    //#endregion

    // 새 custom zone 생성
    const newCustomZone = {
      id: uuidv4(),
      title: file.name.replace(/\.[^/.]+$/, ''),
      color: getRandomHexColor(),
      features: jsonData.features,
      checksum,
      on: false,
    };

    // state 업데이트 및 저장
    const nextCustomZones = [...customZones, newCustomZone];
    setCustomZones(nextCustomZones);
    saveCustomZonesToStorage(nextCustomZones);

    notifier.success(`Custom zone added successfully.`);
    e.target.value = '';
  };

  const deleteCustomZone = (e, zone, index) => {
    e.stopPropagation();

    if (zone.on) {
      hideZone(zone);
    }

    const nextCustomZones = customZones.filter((_, i) => i !== index);
    setCustomZones(nextCustomZones);
    saveCustomZonesToStorage(nextCustomZones);
  };

  const unfocusPanel = (e) => {
    if (menuRef.current.contains(e.target)) return;
    if (panelRef.current.contains(e.target)) return;

    setShowPanel(false);
  };

  const togglePanel = () => {
    if (store.getState().map3d.isShow) {
      notifier.error('This feature is not available in 3D map.');
      return;
    }

    setShowPanel(!showPanel);
  };

  const selectCustomZoneFile = () => {
    fileInputRef.current.click();
  };

  return (
    <div className={cx('container')}>
      <div ref={menuRef} className={cx('menu')} onClick={togglePanel}>
        <RiMapPin5Line size={20} color="white" />
        <div className={cx('name')}>Zones {count > 0 && `(${count})`}</div>
        <AccordionCaret up={showPanel} size={32} />
      </div>
      <div ref={panelRef} className={cx(['panel', { show: showPanel }])}>
        {isKoreanLocale && (
          <div className={cx('local')}>
            {localZones.map((zone, index) => (
              <div key={index} onClick={(e) => toggleZone(e, zone)} className={cx('menu')}>
                {zone.on && <RiCheckboxFill size={16} color={zone.color} />}
                {!zone.on && <RiCheckboxBlankLine size={16} color="white" />}
                <div className={cx(['name', { active: zone.on }])}>{zone.title}</div>
              </div>
            ))}
          </div>
        )}
        <div className={cx('custom')}>
          <div className={cx('header')}>
            <div className={cx('title')}>Custom Zones</div>
            <div className={cx('button')} onClick={selectCustomZoneFile}>
              <RiAddLine size={16} color="white" />
            </div>
          </div>
          <div className={cx('content')}>
            {customZones.length === 0 && (
              <div className={cx('empty')}>
                <EmptyList message="No items" />
              </div>
            )}
            {customZones.map((zone, index) => (
              <div key={index} onClick={(e) => toggleZone(e, zone, true)} className={cx('menu')}>
                {zone.on && <RiCheckboxFill size={16} color={zone.color} />}
                {!zone.on && <RiCheckboxBlankLine size={16} color="white" />}
                <div className={cx(['name', { active: zone.on }])}>{zone.title}</div>
                <div className={cx('button')} onClick={(e) => deleteCustomZone(e, zone, index)}>
                  <RiDeleteBin2Line size={16} color="white" />
                </div>
              </div>
            ))}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={uploadCustomZoneFile}
        />
      </div>
    </div>
  );
};

export default Zones;
